/**
 * @file useAvailableSlots.js
 *
 * Available Slots Hook
 *
 * Fetches computed available time slots for a professional on a specific date.
 * Re-fetches automatically when professionalId or date changes.
 * Returns empty array when either param is missing.
 */
import { useState, useEffect, useCallback } from "react";
import { scheduleService } from "../services";

/**
 * Hook that fetches computed available time slots for a given professional and date.
 *
 * @param {string|number} professionalId - Professional to query slots for
 * @param {string} date - ISO date string (YYYY-MM-DD)
 * @returns {{slots: Array, isLoading: boolean, error: Error|null, refresh: Function}}
 */
export function useAvailableSlots(professionalId, date) {
  const [slots, setSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSlots = useCallback(async () => {
    // Both params required — clear slots if either is missing
    if (!professionalId || !date) {
      setSlots([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await scheduleService.getAvailableSlots(professionalId, date);
      setSlots(response.data || []);
    } catch (err) {
      setError(err);
      setSlots([]);
    } finally {
      setIsLoading(false);
    }
  }, [professionalId, date]);

  // Re-fetch when professional or date changes
  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  return { slots, isLoading, error, refresh: fetchSlots };
}
